<script setup>
import { onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'
import NavBar from '../shared/components/common/NavBar.vue'
import brandLogo from '@/assets/images/BCS-Branding.svg'
import { useRouter, useRoute } from 'vue-router'
import HeroBanner from '../shared/components/LandingPage/HeroBanner.vue'
import WhyBcsPro from '../shared/components/LandingPage/WhyBcsPro.vue'
import OurProduct from '../shared/components/LandingPage/OurProduct.vue'
import TestimonialsSection from '../shared/components/LandingPage/TestimonialsSection.vue'
import FoundersSection from '../shared/components/LandingPage/FoundersSection.vue'
import FaqsLandingSection from '../shared/components/LandingPage/FaqsLandingSection.vue'
import RequestADemo from '../shared/components/LandingPage/RequestADemo.vue'
import LandingPageFooter from '../shared/components/LandingPage/LandingPageFooter.vue'
import { useHamburguerMenuStore } from '../shared/stores/HamburguerMenu'
import { storeToRefs } from 'pinia'
import { SALES_NAV_ITEMS, SALES_ACTION_ITEMS } from '../shared/config/salesLandingNav'

const router = useRouter()
const route = useRoute()
const hamburguerMenuStore = useHamburguerMenuStore()
const { isMenuOpen } = storeToRefs(hamburguerMenuStore)
const { closeMenu } = hamburguerMenuStore

const activePage = ref('')

const navItems = SALES_NAV_ITEMS
const actionItems = SALES_ACTION_ITEMS

const disableScroll = () => {
    document.body.style.overflow = 'hidden';
}

const enableScroll = () => {
    document.body.style.removeProperty('overflow');
}

window.addEventListener('resize', () => {
    if (window.innerWidth > 1030) {
        enableScroll()
        closeMenu()
    }
})

watch(isMenuOpen, (newVal) => {
    if (newVal) {
        disableScroll()
    } else {
        enableScroll()
    }
});

const scrollToElement = (elementId, offset = 0) => {
    const element = document.getElementById(elementId)
    if (element) {
        const offsetTop = element.getBoundingClientRect().top + window.scrollY - offset
        window.scrollTo({ top: offsetTop, behavior: 'smooth' })
        return true
    }
    return false
}

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Each landing section (WhyBcsPro, OurProduct, Testimonials, Founders, FAQs)
// fetches its own content/images from Firebase independently, so there is no
// single point in time when "everything is loaded". Continuously re-aligning
// the scroll on every layout change caused a visible "trembling" effect while
// content was still popping in. Instead, each section emits 'ready' once its
// own data fetch resolves; we wait for all of them (or a safety timeout) and
// then perform a single scroll.
const EXPECTED_READY_SECTIONS = SALES_NAV_ITEMS.map(item => item.id)
const readySections = ref(new Set())
const pendingScrollTarget = ref(null)
let scrollFallbackTimer = null

const clearScrollFallback = () => {
    if (scrollFallbackTimer) {
        clearTimeout(scrollFallbackTimer)
        scrollFallbackTimer = null
    }
}

const performPendingScroll = () => {
    const target = pendingScrollTarget.value
    if (!target) return
    pendingScrollTarget.value = null
    clearScrollFallback()
    scrollToElement(target, 80)
}

const markSectionReady = (sectionId) => {
    readySections.value.add(sectionId)
    if (pendingScrollTarget.value && EXPECTED_READY_SECTIONS.every(id => readySections.value.has(id))) {
        performPendingScroll()
    }
}

const scrollToElementWhenSectionsReady = (elementId, fallbackTimeout = 6000) => {
    pendingScrollTarget.value = elementId
    if (EXPECTED_READY_SECTIONS.every(id => readySections.value.has(id))) {
        performPendingScroll()
        return
    }
    clearScrollFallback()
    scrollFallbackTimer = setTimeout(performPendingScroll, fallbackTimeout)
}

const handleNavClick = (item) => {
    activePage.value = item.id
    if (isMenuOpen.value) {
        closeMenu()
        setTimeout(() => {
            scrollToElement(item.target, 80)
        }, 100)
    } else {
        scrollToElement(item.target, 80)
    }
}

const handleLogoClick = () => {
    activePage.value = ''
    if (isMenuOpen.value) {
        closeMenu()
        setTimeout(() => {
            scrollToTop()
        }, 100)
    } else {
        scrollToTop()
    }
}

const closeMenuFromOverlay = () => {
    closeMenu()
}

const handleActionClick = (item) => {
    if (item.id === 'request-demo') {
        if (isMenuOpen.value) {
            closeMenu()
            setTimeout(() => {
                scrollToElement('request-demo')
            }, 100)
        } else {
            scrollToElement('request-demo')
        }
    } else if (item.id === 'signin') {
        closeMenu();
        router.push({ name: 'login', query: route.query })
    }
}

const handleScroll = () => {
    if (isMenuOpen.value) return;

    const sections = navItems.map(item => {
        const element = document.getElementById(item.target)
        return { id: item.id, element }
    }).filter(section => section.element !== null)

    if (window.scrollY < 100) {
        activePage.value = ''
        return
    }
    const faqsSection = sections.find(section => section.id === 'FAQs')

    if (faqsSection) {
        const faqsRect = faqsSection.element.getBoundingClientRect()
        if (faqsRect.bottom < 500) {
            activePage.value = ''
            return
        }
    }

    const viewportHeight = window.innerHeight
    let currentSection = null
    let maxVisiblePercentage = 0

    sections.forEach(section => {
        const rect = section.element.getBoundingClientRect()
        const visibleTop = Math.max(0, rect.top)
        const visibleBottom = Math.min(viewportHeight, rect.bottom)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)

        const sectionHeight = rect.height
        const visiblePercentage = (visibleHeight / sectionHeight) * 100

        if ((visiblePercentage > maxVisiblePercentage) ||
            (rect.top <= 100 && rect.bottom >= 100)) {
            maxVisiblePercentage = visiblePercentage
            currentSection = section.id
        }
    })

    if (currentSection) {
        activePage.value = currentSection
    }
}

onMounted(() => {
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    if (isMenuOpen.value) {
        disableScroll();
    }
    const scrollTo = route.query.scrollTo
    if (scrollTo) {
        nextTick(() => {
            scrollToElementWhenSectionsReady(scrollTo)
        })
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    if (isMenuOpen.value) {
        enableScroll()
    }
    clearScrollFallback()
})
</script>

<template>
    <div class="sales-page">
        <NavBar :activePage="activePage" :logoSrc="brandLogo" logoAlt="BCS Pro logo" :userData="{}" navMode="scroll"
            navStyle="landing" :navItems="navItems" :actionItems="actionItems" @nav-click="handleNavClick"
            @action-click="handleActionClick" @logo-click="handleLogoClick" />

        <section>
            <HeroBanner />
        </section>
        <!-- Why BCSPro -->
        <section id="why-bcspro">
            <WhyBcsPro @ready="markSectionReady('why-bcspro')" />
        </section>
        <!-- Our Product -->
        <section id="our-product">
            <OurProduct @ready="markSectionReady('our-product')" />
        </section>

        <!-- What People Are Saying -->
        <section id="testimonials">
            <TestimonialsSection @ready="markSectionReady('testimonials')" />
        </section>
        <!-- Founders -->
        <section id="founders-note">
            <FoundersSection @ready="markSectionReady('founders-note')" />
        </section>

        <section id="FAQs">
            <FaqsLandingSection @ready="markSectionReady('FAQs')" />
        </section>

        <section id="request-demo">
            <RequestADemo />
        </section>

        <LandingPageFooter />

    </div>

    <div v-if="isMenuOpen" class="overlay" @click="closeMenuFromOverlay"></div>
</template>

<style>
h3 {
    font-size: 30px;
    font-weight: 500;
}
</style>

<style scoped>
@media (max-width: 1030px) {
    .overlay {
        background-color: rgba(255, 255, 255, 0.8);
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 500;
        right: 0;
        bottom: 0;
        cursor: pointer;
    }

    .sales-page {
        position: relative;
    }
}
</style>
